/**
 * useResolvedFilterIds — watches filter form fields, debounces them,
 * and resolves selected values to their IDs using the provided options.
 *
 * Shared by: WorkOrderAccordion, ScheduleStopDialog, and any component
 * that needs debounced filter → ID resolution for API calls.
 *
 * @param {Object} filterOptions - { measureOptions, materialOptions, serviceActionOptions, businessLineOptions }
 * @param {number} [debounceMs=500] - debounce delay in milliseconds
 * @returns {{ measureIds: string[], materialIds: string[], actionIds: string[], businessLineIds: string[] }}
 */
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useDebounced } from './useDebounced'
import { resolveIds } from '../utils'

const useResolvedFilterIds = (externalWatch, filterOptions = {}, debounceMs = 500) => {
  const formContext = useFormContext()
  const watch = externalWatch || formContext?.watch

  const measureVals = watch('measure')
  const materialVals = watch('material')
  const actionVals = watch('action')
  const businessLineVals = watch('businessLine')
  const radiusCommitted = watch('radiusCommitted')

  const debouncedMeasureVals = useDebounced(measureVals, debounceMs)
  const debouncedMaterialVals = useDebounced(materialVals, debounceMs)
  const debouncedActionVals = useDebounced(actionVals, debounceMs)
  const debouncedBusinessLineVals = useDebounced(businessLineVals, debounceMs)

  const resolved = useMemo(
    () => ({
      measureIds: resolveIds(debouncedMeasureVals, filterOptions.measureOptions),
      materialIds: resolveIds(debouncedMaterialVals, filterOptions.materialOptions),
      actionIds: resolveIds(debouncedActionVals, filterOptions.serviceActionOptions),
      businessLineIds: resolveIds(debouncedBusinessLineVals, filterOptions.businessLineOptions),
    }),
    [debouncedMeasureVals, debouncedMaterialVals, debouncedActionVals, debouncedBusinessLineVals, filterOptions]
  )

  return { ...resolved, radiusCommitted }
}

export default useResolvedFilterIds
